import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Card,
  CardContent,
  CardActionArea,
  Fade,
  Backdrop,
  Modal,
  Button,
} from '@mui/material';
import FileViewer from './FileViewer';
import PDFViewer from './PDFViewer';
import DOCXViewer from './DOCXViewer';
import {
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Description as DescriptionIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  TableChart as SpreadsheetIcon,
  Slideshow as PresentationIcon,
  TextSnippet as DocumentIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

interface DocumentMessageProps {
  documentUrl: string;
  documentPath?: string;
  documentFilename?: string;
  documentSize?: number;
  documentMimeType?: string;
  documentExtension?: string;
  documentCategory?: string;
  content?: string; // Description
  isOwnMessage: boolean;
  timestamp: string;
  senderName?: string;
}

const DocumentMessage: React.FC<DocumentMessageProps> = ({
  documentUrl,
  documentPath,
  documentFilename,
  documentSize,
  documentMimeType,
  documentExtension,
  documentCategory,
  content,
  isOwnMessage,
  timestamp,
  senderName,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [docxViewerOpen, setDocxViewerOpen] = useState(false);

  const getCategoryIcon = (category?: string) => {
    switch (category) {
      case 'document': return <DocumentIcon />;
      case 'presentation': return <PresentationIcon />;
      case 'spreadsheet': return <SpreadsheetIcon />;
      case 'pdf': return <PdfIcon />;
      case 'data': return <FileIcon />;
      default: return <FileIcon />;
    }
  };

  const getCategoryColor = (category?: string): string => {
    switch (category) {
      case 'document': return '#1976d2';
      case 'presentation': return '#9c27b0';
      case 'spreadsheet': return '#2e7d32';
      case 'pdf': return '#d32f2f';
      case 'data': return '#ed6c02';
      default: return '#666666';
    }
  };

  const getCategoryLabel = (category?: string): string => {
    switch (category) {
      case 'document': return 'Document';
      case 'presentation': return 'Presentation';
      case 'spreadsheet': return 'Spreadsheet';
      case 'pdf': return 'PDF';
      case 'data': return 'Data File';
      default: return 'File';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(documentUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = documentFilename || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  const isViewableFile = (): boolean => {
    const fileName = documentFilename?.toLowerCase() || '';
    const mimeType = documentMimeType?.toLowerCase() || '';
    
    return (
      fileName.endsWith('.csv') ||
      fileName.endsWith('.xlsx') ||
      fileName.endsWith('.xls') ||
      mimeType.includes('csv') ||
      mimeType.includes('sheet') ||
      mimeType.includes('excel')
    );
  };

  const isPDFFile = (): boolean => {
    const fileName = documentFilename?.toLowerCase() || '';
    const mimeType = documentMimeType?.toLowerCase() || '';
    
    return (
      fileName.endsWith('.pdf') ||
      mimeType.includes('pdf')
    );
  };

  const isDOCXFile = (): boolean => {
    const fileName = documentFilename?.toLowerCase() || '';
    const mimeType = documentMimeType?.toLowerCase() || '';
    const category = documentCategory?.toLowerCase() || '';
    
    console.log('DOCX Detection Debug:', {
      fileName,
      mimeType,
      category,
      documentFilename,
      documentMimeType,
      documentCategory,
      isDocx: fileName.endsWith('.docx') ||
              fileName.endsWith('.doc') ||
              mimeType.includes('wordprocessingml') ||
              mimeType.includes('msword') ||
              category === 'document'
    });
    
    return (
      fileName.endsWith('.docx') ||
      fileName.endsWith('.doc') ||
      mimeType.includes('wordprocessingml') ||
      mimeType.includes('msword') ||
      category === 'document'
    );
  };

  const handlePreview = () => {
    console.log('Preview Debug:', {
      isPDF: isPDFFile(),
      isDOCX: isDOCXFile(),
      isViewable: isViewableFile(),
      filename: documentFilename,
      mimeType: documentMimeType,
      category: documentCategory
    });
    
    // Force DOCX detection for .docx files
    const isDocxByExtension = documentFilename?.toLowerCase().endsWith('.docx') || 
                             documentFilename?.toLowerCase().endsWith('.doc');
    
    if (isPDFFile()) {
      console.log('Opening PDF viewer');
      setPdfViewerOpen(true);
    } else if (isDOCXFile() || isDocxByExtension) {
      console.log('Opening DOCX viewer');
      setDocxViewerOpen(true);
    } else if (isViewableFile()) {
      console.log('Opening file viewer');
      setFileViewerOpen(true);
    } else {
      console.log('Opening preview modal');
      setPreviewOpen(true);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        style={{
          display: 'flex',
          justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
          marginBottom: '8px',
        }}
      >
        <Paper
          elevation={2}
          sx={{
            maxWidth: 400,
            minWidth: 280,
            borderRadius: 3,
            overflow: 'hidden',
            background: isOwnMessage 
              ? 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)'
              : 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
            position: 'relative',
          }}
        >
          <CardActionArea onClick={handlePreview}>
            <CardContent sx={{ p: 2 }}>
              {/* Document Header */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                mb: 1
              }}>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: isOwnMessage ? 'white' : getCategoryColor(documentCategory),
                }}>
                  {getCategoryIcon(documentCategory)}
                </Box>
                
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography 
                    variant="subtitle1" 
                    fontWeight="bold"
                    sx={{ 
                      color: isOwnMessage ? 'white' : 'text.primary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      mb: 0.5
                    }}
                  >
                    {documentFilename || 'Document'}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={getCategoryLabel(documentCategory)}
                      size="small"
                      sx={{
                        background: isOwnMessage ? 'rgba(255,255,255,0.2)' : getCategoryColor(documentCategory),
                        color: isOwnMessage ? 'white' : 'white',
                        fontSize: '0.7rem',
                        height: 20,
                      }}
                    />
                    {documentSize && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: isOwnMessage ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                          fontSize: '0.7rem'
                        }}
                      >
                        {formatFileSize(documentSize)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Description */}
              {content && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: isOwnMessage ? 'rgba(255,255,255,0.9)' : 'text.secondary',
                    mt: 1,
                    fontStyle: 'italic'
                  }}
                >
                  "{content}"
                </Typography>
              )}

              {/* Action Buttons */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end',
                gap: 1,
                mt: 2,
                opacity: 0,
                transition: 'opacity 0.2s ease',
                '&:hover': {
                  opacity: 1,
                },
              }}>
                <Tooltip title={
                  isPDFFile() ? "Preview PDF" : 
                  (isDOCXFile() || documentFilename?.toLowerCase().endsWith('.docx') || documentFilename?.toLowerCase().endsWith('.doc')) ? "Preview Word Document" :
                  isViewableFile() ? "Preview Data" : 
                  "Preview Info"
                }>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreview();
                    }}
                    sx={{
                      background: 'rgba(0, 0, 0, 0.1)',
                      color: isOwnMessage ? 'white' : 'text.primary',
                      '&:hover': {
                        background: 'rgba(0, 0, 0, 0.2)',
                      },
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Download">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload();
                    }}
                    sx={{
                      background: 'rgba(0, 0, 0, 0.1)',
                      color: isOwnMessage ? 'white' : 'text.primary',
                      '&:hover': {
                        background: 'rgba(0, 0, 0, 0.2)',
                      },
                    }}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </CardContent>
          </CardActionArea>
        </Paper>
      </motion.div>

      {/* Document Preview Modal */}
      <Modal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { background: 'rgba(0, 0, 0, 0.9)' },
        }}
      >
        <Fade in={previewOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxWidth: '90vw',
              maxHeight: '90vh',
              outline: 'none',
              background: 'white',
              borderRadius: 2,
              p: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: 2,
                background: getCategoryColor(documentCategory),
                color: 'white',
              }}>
                {getCategoryIcon(documentCategory)}
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {documentFilename || 'Document'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getCategoryLabel(documentCategory)} • {documentSize ? formatFileSize(documentSize) : 'Unknown size'}
                </Typography>
              </Box>
            </Box>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Document preview is not available. Click download to view the file.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                onClick={() => setPreviewOpen(false)}
                variant="outlined"
              >
                Close
              </Button>
              <Button
                onClick={handleDownload}
                variant="contained"
                startIcon={<DownloadIcon />}
              >
                Download
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* File Viewer for CSV/Excel files */}
      <FileViewer
        open={fileViewerOpen}
        onClose={() => setFileViewerOpen(false)}
        fileUrl={documentUrl}
        fileName={documentFilename || 'document'}
        fileType={documentMimeType || ''}
        fileSize={documentSize}
      />

      {/* PDF Viewer for PDF files */}
      <PDFViewer
        open={pdfViewerOpen}
        onClose={() => setPdfViewerOpen(false)}
        fileUrl={documentUrl}
        fileName={documentFilename || 'document'}
        fileSize={documentSize}
      />

      {/* DOCX Viewer for Word documents */}
      <DOCXViewer
        open={docxViewerOpen}
        onClose={() => setDocxViewerOpen(false)}
        fileUrl={documentUrl}
        fileName={documentFilename || 'document'}
        fileSize={documentSize}
      />
    </>
  );
};

export default DocumentMessage;
